import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Badge } from "@/components/base/badges/badges";
import { ChevronDown, SearchLg } from "@untitledui/icons";
import type { Agent } from "@/types/workflow";
import { useTranslation } from "react-i18next";
import { cx } from "@/utils/cx";

interface AgentSelectorProps {
    selectedAgentId?: string;
    agents: Agent[];
    onSelect: (agentId: string) => void;
    isLoading?: boolean;
}

export const AgentSelector = ({ selectedAgentId, agents, onSelect, isLoading }: AgentSelectorProps) => {
    const { t } = useTranslation(['pages:workflows-admin', 'common']);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const [popoverWidth, setPopoverWidth] = useState("");

    const selectedAgent = useMemo(() => {
        return agents.find((agent) => agent.id === selectedAgentId);
    }, [agents, selectedAgentId]);

    // Filtra gli agenti in base a ricerca e filtri
    const filteredAgents = useMemo(() => {
        let filtered = agents;

        // Applica filtro selezionato
        if (selectedFilter === "default") {
            filtered = filtered.filter((agent) => agent.is_default);
        } else if (selectedFilter && selectedFilter !== "all" && selectedFilter !== null) {
            filtered = filtered.filter((agent) => agent.agent_type === selectedFilter);
        }

        // Applica ricerca
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter((agent) => {
                const nameMatch = agent.name.toLowerCase().includes(searchLower);
                const descMatch = agent.description?.toLowerCase().includes(searchLower);
                const specMatch = agent.specialization?.some((spec) => spec.toLowerCase().includes(searchLower));
                return nameMatch || descMatch || specMatch;
            });
        }

        return filtered;
    }, [agents, searchTerm, selectedFilter]);

    // Ottieni agent_type unici per i filtri
    const agentTypes = useMemo(() => {
        const types = new Set<string>();
        agents.forEach((agent) => {
            if (agent.agent_type) {
                types.add(agent.agent_type);
            }
        });
        return Array.from(types);
    }, [agents]);

    const handleSelect = useCallback((agentId: string) => {
        onSelect(agentId);
        setIsOpen(false);
        setSearchTerm("");
    }, [onSelect]);

    const handleTriggerClick = useCallback(() => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPopoverWidth(`${rect.width}px`);
        }
        setIsOpen(!isOpen);
    }, [isOpen]);

    // Gestione click outside per chiudere il popover
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                popoverRef.current &&
                triggerRef.current &&
                !popoverRef.current.contains(event.target as Node) &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setSearchTerm("");
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [isOpen]);

    return (
        <div className="relative">
            <Button
                ref={triggerRef}
                onClick={handleTriggerClick}
                color="secondary"
                size="md"
                className="w-full justify-between"
                iconTrailing={ChevronDown}
                isDisabled={isLoading}
            >
                {selectedAgent ? (
                    <span className="truncate">{selectedAgent.name}</span>
                ) : (
                    <span className="text-placeholder">{t('workflows.editor.agentSelector.placeholder')}</span>
                )}
            </Button>

            {isOpen && (
                <div
                    ref={popoverRef}
                    className="absolute z-50 mt-2 rounded-lg bg-primary shadow-lg ring-1 ring-secondary_alt overflow-hidden"
                    style={{ width: popoverWidth || '400px', minWidth: '400px', maxHeight: '600px' }}
                >
                    <div className="flex flex-col">
                        {/* Search Input */}
                        <div className="p-4 border-b border-secondary">
                            <Input
                                value={searchTerm}
                                onChange={setSearchTerm}
                                placeholder={t('workflows.editor.agentSelector.searchPlaceholder')}
                                icon={SearchLg}
                                autoFocus
                            />
                        </div>

                        {/* Filters */}
                        {(agentTypes.length > 0 || agents.some(a => a.is_default)) && (
                            <div className="p-3 border-b border-secondary flex flex-wrap gap-2">
                                <button
                                    onClick={() => setSelectedFilter(null)}
                                    className={cx(
                                        "px-3 py-1 rounded-md text-xs font-medium transition-colors",
                                        selectedFilter === null
                                            ? "bg-brand text-white"
                                            : "bg-secondary text-tertiary hover:bg-primary"
                                    )}
                                >
                                    {t('workflows.editor.agentSelector.filters.all')}
                                </button>
                                {agents.some(a => a.is_default) && (
                                    <button
                                        onClick={() => setSelectedFilter(selectedFilter === "default" ? null : "default")}
                                        className={cx(
                                            "px-3 py-1 rounded-md text-xs font-medium transition-colors",
                                            selectedFilter === "default"
                                                ? "bg-brand text-white"
                                                : "bg-secondary text-tertiary hover:bg-primary"
                                        )}
                                    >
                                        {t('workflows.editor.agentSelector.filters.defaultOnly')}
                                    </button>
                                )}
                                {agentTypes.map((type) => (
                                    <button
                                        key={type}
                                        onClick={() => setSelectedFilter(selectedFilter === type ? null : type)}
                                        className={cx(
                                            "px-3 py-1 rounded-md text-xs font-medium transition-colors",
                                            selectedFilter === type
                                                ? "bg-brand text-white"
                                                : "bg-secondary text-tertiary hover:bg-primary"
                                        )}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Agents List */}
                        <div className="overflow-y-auto max-h-[500px] p-4 space-y-2">
                            {isLoading ? (
                                <div className="text-center py-8 text-tertiary">
                                    {t('common:loading')}
                                </div>
                            ) : filteredAgents.length === 0 ? (
                                <div className="text-center py-8 text-tertiary">
                                    {t('workflows.editor.agentSelector.noResults')}
                                </div>
                            ) : (
                                filteredAgents.map((agent) => (
                                    <div
                                        key={agent.id}
                                        onClick={() => handleSelect(agent.id)}
                                        className={cx(
                                            "p-4 rounded-lg border cursor-pointer transition-colors",
                                            selectedAgentId === agent.id
                                                ? "bg-primary border-brand ring-1 ring-brand"
                                                : "bg-primary border-secondary hover:border-brand hover:ring-1 hover:ring-brand"
                                        )}
                                    >
                                        <div className="flex flex-col gap-2">
                                            {/* Header */}
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold text-primary">{agent.name}</span>
                                                {agent.agent_type && (
                                                    <Badge color="blue" size="sm">
                                                        {agent.agent_type}
                                                    </Badge>
                                                )}
                                                {agent.is_default && (
                                                    <Badge color="success" size="sm">
                                                        {t('workflows.editor.agentDefault')}
                                                    </Badge>
                                                )}
                                            </div>

                                            {/* Description */}
                                            {agent.description && (
                                                <p className="text-sm text-tertiary line-clamp-2">{agent.description}</p>
                                            )}

                                            {/* Specialization */}
                                            {agent.specialization && agent.specialization.length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {agent.specialization.slice(0, 3).map((spec, idx) => (
                                                        <Badge key={idx} color="gray" size="sm">
                                                            {spec}
                                                        </Badge>
                                                    ))}
                                                    {agent.specialization.length > 3 && (
                                                        <Badge color="gray" size="sm">
                                                            +{agent.specialization.length - 3}
                                                        </Badge>
                                                    )}
                                                </div>
                                            )}

                                            {/* Metadata row: Tools and RAG sources */}
                                            {(agent.tools || agent.rag_sources) && (
                                                <div className="flex items-center gap-3 text-xs text-tertiary mt-1">
                                                    {agent.tools && agent.tools.length > 0 && (
                                                        <span>
                                                            🔧 {agent.tools.length} {t('workflows.editor.agentSelector.tools', { count: agent.tools.length })}
                                                        </span>
                                                    )}
                                                    {agent.rag_sources && agent.rag_sources.length > 0 && (
                                                        <span>
                                                            📚 {agent.rag_sources.length} {t('workflows.editor.agentSelector.ragSources', { count: agent.rag_sources.length })}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
